import { useMemo, useState, useEffect } from "react";
import _ from "lodash";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  LinearProgress,
  Stack,
  useTheme,
} from "@mui/material";
import Icon from "../../../../Icon/Icon";
import { AvatarGroup } from "../../../../MainComponents/Avatar/AvatarGroup";
import { EGSpeedDial } from "../../../../MainComponents/Button/SpeedDial/SpeedDial";
import { InvestigationBlockInfo } from "../components/InvestigationBlockInfo";
import { ActivityInfo } from "./Modals/ActivityInfo";
import { calculateTimeProgress } from "../helpers/calculateTimeProgress";
import { useSelector } from "react-redux";
import { useConstants } from "../Main/useConstants";
import { Avatars } from "../components/InvestigationComponents";
import Comments from "../../Information/Comments";
import { catalogByPart } from "../../../../../apis";

// "completed" | "process" | "pending"

export const Board = ({
  plans,
  setPlans,
  selectedActivity,
  setSelectedActivity,
  setIsEditing,
  handleSavePlans,
  justView,
}) => {
  const theme = useTheme();
  const [t] = useTranslation("investigation");

  const { enums } = useConstants();
  const jaguarState = useSelector((state) => state.jaguar);

  const [showDetailsActivity, setShowDetailsActivity] = useState(false);
  const [activityPlanOptions, setActivityPlanOptions] = useState([]);

  useEffect(() => {
    const loadActivityPlanOptions = async () => {
      try {
        const response = await catalogByPart({
          catalogue: "activityPlan",
          is_own: true,
          path: "/"
        });
        if (response?.data) {
          const options = response.data.map((item) => ({
            label: item.label || item.value,
            value: item.path_locales || item.value,
          }));
          setActivityPlanOptions(options);
        }
      } catch (error) {
        console.error('Error loading activityPlan catalogue:', error);
      }
    };

    loadActivityPlanOptions();
  }, []);

  const getActivityNameLabel = (nameValue) => {
    if (!nameValue) return "";
    const found = activityPlanOptions.find(opt => opt.value === nameValue);
    return found ? found.label : nameValue;
  };

  const { completed, process, pending } = useMemo(() => {
    const arrays = {
      completed: [],
      process: [],
      pending: [],
    };
    plans.forEach((plan, trueIdx) => {
      if (plan.status === "completed") {
        arrays.completed.push({ plan, trueIdx });
      } else if (plan.status === "process") {
        arrays.process.push({ plan, trueIdx });
      } else if (plan.status === "pending") {
        arrays.pending.push({ plan, trueIdx });
      }
    });
    return arrays;
  }, [plans]);

  const columns = [
    {
      id: "pending",
      title: t("activityPlan.new"),
      color: theme.palette.primary.main,
      plans: pending,
    },
    {
      id: "process",
      title: t("activityPlan.in_process"),
      color: theme.palette.purple.main,
      plans: process,
    },
    {
      id: "completed",
      title: t("activityPlan.completed"),
      color: theme.palette.green.main,
      plans: completed,
    },
  ];

  const onDragEnd = (result) => {
    // Si no hay cambio de posición
    const { source, destination } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    )
      return;

    // Obtener las columnas de origen y destino
    const sourceCol = columns.find((col) => col.id === source.droppableId);
    const destCol = columns.find((col) => col.id === destination.droppableId);

    // Obtener los planes de origen y destino
    const sourcePlans = [...sourceCol.plans];
    const destPlans = sourceCol === destCol ? sourcePlans : [...destCol.plans];

    // Obtener el plan movido
    const moved = sourcePlans[source.index];
    const newPlans = [...plans];

    if (sourceCol === destCol) {
      // Reordenar dentro de la misma columna
      sourcePlans.splice(source.index, 1);
      sourcePlans.splice(destination.index, 0, moved);
      // Reconstruir el array original respetando el nuevo orden de la columna
      let colIdx = 0;
      for (let i = 0; i < newPlans.length; i++) {
        if (sourceCol.id === newPlans[i].status) {
          newPlans[i] = sourcePlans[colIdx].plan;
          colIdx++;
        }
      }
      setPlans(newPlans);
      handleSavePlans && handleSavePlans(newPlans);
      return;
    }

    // Mover entre columnas
    // 1. Eliminar de la columna origen
    sourcePlans.splice(source.index, 1);
    // 2. Cambiar el status del plan movido
    const updatedMoved = { ...moved.plan, status: destCol.id };
    // 3. Insertar en la columna destino en la posición indicada
    destPlans.splice(destination.index, 0, {
      plan: updatedMoved,
      trueIdx: moved.trueIdx,
    });

    // 4. Reconstruir el array original:
    //   - Primero, eliminar el plan movido de su posición original
    //   - Luego, insertar el plan actualizado en la posición correcta de la columna destino
    //   - Mantener el orden de los demás elementos
    let resultPlans = [];
    // Recorremos todas las columnas en orden original
    columns.forEach((col) => {
      let colArray = [];
      if (col.id === sourceCol.id) {
        colArray = sourcePlans.map((item) => item.plan);
      } else if (col.id === destCol.id) {
        colArray = destPlans.map((item) => item.plan);
      } else {
        colArray = newPlans.filter((p) => p.status === col.id);
      }
      resultPlans = resultPlans.concat(colArray);
    });
    setPlans(resultPlans);
    handleSavePlans && handleSavePlans(resultPlans);
  };

  const handleToggleTodo = async (todoIdx, planIdx) => {
    const newPlans = _.cloneDeep(plans);
    const planTodo = newPlans[planIdx].todos[todoIdx];
    if (typeof planTodo === "string") {
      newPlans[planIdx].todos[todoIdx] = {
        name: planTodo,
        completed: true,
      };
    } else {
      planTodo.completed = !planTodo.completed;
    }
    handleSavePlans && (await handleSavePlans(newPlans));
  };

  const actions = [
    {
      name: t("activityPlan.details"),
      icon: <Icon name="outline_eye" color={theme.palette.primary.main} />,
      onClick: (_, trueIdx) => {
        setSelectedActivity(trueIdx);
        setShowDetailsActivity(true);
      },
    },
    {
      name: t("activityPlan.delete"),
      icon: <Icon name="trash_can" color={theme.palette.error.main} />,
      onClick: (_, trueIdx) => {
        const newPlans = plans.filter((_, idx) => idx !== trueIdx);
        setPlans(newPlans);
        handleSavePlans && handleSavePlans(newPlans);
      },
    },
  ];

  return (
    <>
      <ActivityInfo // MODAL
        open={showDetailsActivity}
        onClose={() => setShowDetailsActivity(false)}
        selectedActivity={selectedActivity}
        setSelectedActivity={setSelectedActivity}
        plans={plans}
        handleSavePlans={handleSavePlans}
        setIsEditing={setIsEditing}
      />
      <DragDropContext onDragEnd={onDragEnd}>
        <Box display="flex" gap={2} width="100%">
          {columns.map((col) => (
            <Box key={col.id} flex={1} minWidth={0}>
              <Box p={2} bgcolor={col.color} borderRadius="8px 8px 0 0">
                <Typography variant="h6" color="#fff" fontWeight={700}>
                  {col.title} ({col.plans.length})
                </Typography>
              </Box>
              <Droppable droppableId={col.id}>
                {(provided, snapshot) => (
                  <Stack
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{
                      background: "#fff",
                      border: `2px solid ${col.color}`,
                      borderTop: `none`,
                      borderRadius: `0 0 4px 4px`,
                      minHeight: 400,
                      padding: 2,
                    }}
                    spacing={2}
                  >
                    {col.plans.map(({ plan, trueIdx }, idx) => (
                      <Draggable
                        key={trueIdx}
                        draggableId={String(trueIdx)}
                        index={idx}
                      >
                        {(provided, snapshot) => (
                          <Plan
                            plan={plan}
                            actions={actions}
                            trueIdx={trueIdx}
                            provided={provided}
                            snapshot={snapshot}
                            handleToggleTodo={handleToggleTodo}
                            getActivityNameLabel={getActivityNameLabel}
                          />
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Stack>
                )}
              </Droppable>
            </Box>
          ))}
        </Box>
      </DragDropContext>
    </>
  );
};

const Plan = ({
  plan,
  actions,
  trueIdx,
  provided,
  snapshot,
  handleToggleTodo,
  getActivityNameLabel,
}) => {
  const theme = useTheme();
  const [t] = useTranslation("investigation");

  const { enums } = useConstants();

  const { daysLeft, totalDays, progress } = useMemo(
    () => calculateTimeProgress(plan),
    [plan]
  );

  return (
    <Box
      ref={provided.innerRef}
      {...provided.draggableProps}
      sx={{
        "&:hover": {
          border: `1px solid ${theme.palette.primary.main}`,
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
          backgroundColor: "blueGray50.main",
        },
        background: snapshot.isDragging ? "#f0f8ff" : "transparent",
        borderRadius: `4px`,
        boxShadow: snapshot.isDragging
          ? "0 4px 12px rgba(0,0,0,0.12)"
          : "0 1px 4px rgba(0,0,0,0.08)",
        border: snapshot.isDragging ? "2px solid #0094e0" : "1px solid #e0e0e0",
        padding: 2,
        transition:
          "border 0.3s ease, box-shadow 0.3s ease, background-color 0.3s ease",
      }}
    >
      {/* HEADER */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        {...provided.dragHandleProps}
      >
        <Typography color="#546F7A" fontSize={16} fontWeight={700}>
          {getActivityNameLabel(plan.name) || t("Sin nombre")}
        </Typography>
        <Box style={{ minWidth: "35px", position: "relative" }}>
          <EGSpeedDial
            direction="left"
            actions={actions}
            props={trueIdx}
            sx={{
              position: "absolute",
              top: "50%",
              right: -15,
              transform: "translateY(calc(-50% - 8px))",
              scale: 0.8,
            }}
          />
        </Box>
      </Stack>

      {
        ((plan.status !== enums.statusPlan.COMPLETED) || (plan.details)) && (
          <div className="hr-div" style={{ marginTop: 20, marginBottom: 10 }}>
            .
          </div>
        )
      }
      {/* {plan.details && (
        <InvestigationBlockInfo
          label={t("Detalles de la actividad")}
          labelColor={theme.palette.blueGray.main}
          text={plan.details}
          size={12}
          underline={false}
        />
      )} */}
      {plan.details && (
        <>
          <Typography fontSize={12} fontWeight={600} color={theme.palette.secondary.main} >
            {t("activityPlan.details_of_the_activity")}
          </Typography>
          <Comments 
            text={plan.details}
            maxLines={3}
            padding="0px 0px"
            showAtEnd={true}
          />
        </>
      )}

      {/* TODOS */}
      {/* {plan.todos && plan.todos.length > 0 && (
        <Box mt={1.4}>
          <Typography variant="subtitle2" fontWeight={600}>
            Tareas de la actividad
          </Typography>
          <Stack mt={0.5} spacing={0.5}>
            {plan.todos &&
              plan.todos.map((todo, i) => (
                <Box
                  key={i}
                  display="grid"
                  gridTemplateColumns="20px 1fr"
                  alignItems="center"
                  gap={0.8}
                  width="fit-content"
                >
                  {todo.completed ? (
                    <Icon
                      name="bold_tick_circle"
                      size={20}
                      color={theme.palette.success.main}
                      onClick={() => handleToggleTodo(i, trueIdx)}
                      style={{ cursor: "pointer" }}
                    />
                  ) : (
                    <Box
                      width={20}
                      height={20}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      onClick={() => handleToggleTodo(i, trueIdx)}
                      style={{ cursor: "pointer" }}
                    >
                      <Box
                        color="transparent"
                        border={`1px solid ${theme.palette.primary.main}`}
                        width={16.9}
                        height={16.9}
                        borderRadius={"50%"}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        component="div"
                      />
                    </Box>
                  )}
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: todo.completed
                        ? "#979797"
                        : theme.palette.oxfordBlue.main,
                    }}
                  >
                    {todo.name ?? todo} {todo.completed && "(Completada)"}
                  </Typography>
                </Box>
              ))}
          </Stack>
        </Box>
      )} */}

      {/* TIEMPO PARA COMPLETAR */}
      {
        plan.status !== "completed" && (
          <Box mt={1.5}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography color="#546F7A" fontSize={11}>
                {t("activityPlan.time_to_complete")}
              </Typography>
              <Typography color="#546F7A" fontWeight={600} fontSize={11}>
                {daysLeft === 0 ? t("activityPlan.expired") : `${daysLeft} ${t("activityPlan.days")}`}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={calculateTimeProgress(plan).progress}
              sx={{
                height: 4,
                borderRadius: 5,
                mt: 0.7,
                mb: 0.3,
                backgroundColor: "gray2.main",
                "& .MuiLinearProgress-bar": {
                  backgroundColor:
                    daysLeft <= 0
                      ? theme.palette.error.main
                      : theme.palette.primary.main,
                },
              }}
            />
            <Box display="flex" justifyContent="space-between">
              <Typography color="#546F7A" fontWeight={600} fontSize={11}>
                {totalDays - daysLeft}
              </Typography>
              <Typography color="#546F7A" fontWeight={600} fontSize={11}>
                {totalDays}
              </Typography>
            </Box>
          </Box>
        )
      }

      {
        ((Array.isArray(plan.todos) && plan.todos.length > 0)
          || (Array.isArray(plan.party) && plan.party.length > 0)
          || plan.assignedTo) && (
          <div className="hr-div" style={{ marginTop: 20, marginBottom: 10 }}>
            .
          </div>
        )
      }

      {/* PERSONAS */}

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        {
          Array.isArray(plan.todos) && plan.todos.length > 0 && (
            <Typography color="#546F7A" fontSize={11}>
              {plan.todos.length} {plan.todos.length === 1 ? t("activityPlan.task") : t("activityPlan.tasks")}
            </Typography>
          )
        }
        <Box></Box>
        {((Array.isArray(plan.party) && plan.party.length > 0) ||
          plan.assignedTo) && (
          <Avatars party={plan.party} assignedTo={plan.assignedTo} />
        )}
      </Stack>

    </Box>
  );
};
